import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Switch,
    FormControlLabel,
    CircularProgress,
    MenuItem
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import MarkdownEditor from '../../../components/MarkdownEditor';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig } from '../../../redux/slices/config';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';
import AppDateTimePicker from '../../../components/AppDateTimePicker';

const TIME_ENTRY_DEFAULTS = { minThresholdMinutes: 5, roundUpToMinutes: 15, maxDurationHours: 24 };

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SectionCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const ActionsRow = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
});

const SystemConfig = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [formData, setFormData] = useState({
        brandName: '',
        basePath: '',
        fixedPriceRate: '',
        defaultMonthlyHours: '',
        defaultDailyHours: '',
        showAccountProgress: false,
        selfRegister: false,
        messageSubject: '',
        messageContent: '',
        messageSeverity: 'info',
        messageStartDate: '',
        messageStopDate: '',
        timeMinThresholdMinutes: String(TIME_ENTRY_DEFAULTS.minThresholdMinutes),
        timeRoundUpToMinutes: String(TIME_ENTRY_DEFAULTS.roundUpToMinutes),
        timeMaxDurationHours: String(TIME_ENTRY_DEFAULTS.maxDurationHours),
    });

    useEffect(() => {
        loadConfig();
    }, []);

    const parseDecimal = (val) => {
        if (val == null) return '';
        if (typeof val === 'object' && val.$numberDecimal) return val.$numberDecimal;
        return String(val);
    };

    const parseDateForInput = (dateVal) => {
        if (!dateVal) return '';
        try {
            const str = String(dateVal);
            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) return str;
            const d = new Date(str);
            if (Number.isNaN(d.getTime())) return '';
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        } catch {
            return '';
        }
    };

    const normalizeDateForSave = (val) => {
        if (!val) return null;
        const str = String(val);
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return `${str}T00:00`;
        return str;
    };

    const loadConfig = () => {
        setLoading(true);
        dispatch(getConfigValue({
            key: 'systemConfig',
            value: null,
            callback: (data) => {
                if (data?.value) {
                    const val = data.value;
                    const msg = val.message || {};
                    const te = val.timeEntry || {};
                    setFormData({
                        brandName: val.brandName || '',
                        basePath: val.basePath || '',
                        fixedPriceRate: parseDecimal(val.fixedPriceRate),
                        defaultMonthlyHours: val.defaultMonthlyHours != null ? String(val.defaultMonthlyHours) : '',
                        defaultDailyHours: val.defaultDailyHours != null ? String(val.defaultDailyHours) : '',
                        showAccountProgress: !!val.showAccountProgress,
                        selfRegister: !!val.selfRegister,
                        messageSubject: msg.subject || '',
                        messageContent: msg.content || '',
                        messageSeverity: msg.severity || 'info',
                        messageStartDate: parseDateForInput(msg.startDate),
                        messageStopDate: parseDateForInput(msg.stopDate),
                        timeMinThresholdMinutes: te.minThresholdMinutes != null ? String(te.minThresholdMinutes) : String(TIME_ENTRY_DEFAULTS.minThresholdMinutes),
                        timeRoundUpToMinutes: te.roundUpToMinutes != null ? String(te.roundUpToMinutes) : String(TIME_ENTRY_DEFAULTS.roundUpToMinutes),
                        timeMaxDurationHours: te.maxDurationHours != null ? String(te.maxDurationHours) : String(TIME_ENTRY_DEFAULTS.maxDurationHours),
                    });
                }
                setLoading(false);
            }
        }));
    };

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const handleChange = (e) => {
        setIsDirty(true);
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = () => {
        const parseIntOrDefault = (val, def) => {
            const n = parseInt(val, 10);
            return Number.isFinite(n) && n >= 0 ? n : def;
        };
        const parseFloatOrDefault = (val, def) => {
            const n = parseFloat(val);
            return Number.isFinite(n) && n > 0 ? n : def;
        };
        const saveData = {
            brandName: formData.brandName.trim(),
            basePath: formData.basePath,
            fixedPriceRate: formData.fixedPriceRate,
            defaultMonthlyHours: formData.defaultMonthlyHours === '' ? null : Number(formData.defaultMonthlyHours),
            defaultDailyHours: formData.defaultDailyHours === '' ? null : Number(formData.defaultDailyHours),
            showAccountProgress: formData.showAccountProgress,
            selfRegister: formData.selfRegister,
            message: {
                subject: formData.messageSubject,
                content: formData.messageContent,
                severity: formData.messageSeverity || 'info',
                startDate: normalizeDateForSave(formData.messageStartDate),
                stopDate: normalizeDateForSave(formData.messageStopDate),
            },
            timeEntry: {
                minThresholdMinutes: parseIntOrDefault(formData.timeMinThresholdMinutes, TIME_ENTRY_DEFAULTS.minThresholdMinutes),
                roundUpToMinutes: parseIntOrDefault(formData.timeRoundUpToMinutes, TIME_ENTRY_DEFAULTS.roundUpToMinutes),
                maxDurationHours: parseFloatOrDefault(formData.timeMaxDurationHours, TIME_ENTRY_DEFAULTS.maxDurationHours),
            },
        };
        setSaving(true);
        dispatch(saveConfig({
            key: 'systemConfig',
            value: saveData,
            callback: (data) => {
                setSaving(false);
                if (data?.status === 0) return;
                setIsDirty(false);
                dispatch(showAlert({ severity: 'success', message: 'System configuration saved successfully' }));
            }
        }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Configuration"
                subtitle="Manage system configuration settings"
            />

            <ConfigSubMenu />

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : (
                <>
                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            General Settings
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Brand Name"
                                    name="brandName"
                                    value={formData.brandName}
                                    onChange={handleChange}
                                    helperText="The product name shown throughout the app, on the sign-in screen, and in emails. Leave blank to use the default."
                                    placeholder="Pollux"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Base Path / URL"
                                    name="basePath"
                                    value={formData.basePath}
                                    onChange={handleChange}
                                    placeholder="https://your-domain.com"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Fixed Price Rate"
                                    name="fixedPriceRate"
                                    type="number"
                                    value={formData.fixedPriceRate}
                                    onChange={handleChange}
                                    inputProps={{ step: '0.01', min: '0' }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Default Monthly Hours"
                                    name="defaultMonthlyHours"
                                    type="number"
                                    value={formData.defaultMonthlyHours}
                                    onChange={handleChange}
                                    inputProps={{ step: '1', min: '0' }}
                                    helperText="Resource Utilization fallback when a user has no salaryData.monthlyHours (default 160)"
                                    placeholder="160"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Default Daily Hours"
                                    name="defaultDailyHours"
                                    type="number"
                                    value={formData.defaultDailyHours}
                                    onChange={handleChange}
                                    inputProps={{ step: '0.5', min: '0' }}
                                    helperText="Daily Hours report target when a user has no salaryData.dailyHours (default 8)"
                                    placeholder="8"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="showAccountProgress"
                                            checked={formData.showAccountProgress}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Show account progress"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="selfRegister"
                                            checked={formData.selfRegister}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Allow self-registration"
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            System Message / Banner
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    label="Subject"
                                    name="messageSubject"
                                    value={formData.messageSubject}
                                    onChange={handleChange}
                                    helperText="Title shown above the message body"
                                    placeholder="Important Notice"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Severity"
                                    name="messageSeverity"
                                    value={formData.messageSeverity || 'info'}
                                    onChange={handleChange}
                                    helperText="Color and icon style of the banner"
                                >
                                    <MenuItem value="info">Info</MenuItem>
                                    <MenuItem value="success">Success</MenuItem>
                                    <MenuItem value="warning">Warning</MenuItem>
                                    <MenuItem value="error">Error</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Message Content
                                </Typography>
                                <MarkdownEditor
                                    key="systemMessageBody"
                                    initialContent={formData.messageContent || null}
                                    onChange={(json) => {
                                        setIsDirty(true);
                                        setFormData(prev => ({ ...prev, messageContent: json }));
                                    }}
                                    autoFocus={false}
                                    placeholder="System-wide banner message shown to all users…"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <AppDateTimePicker
                                    label="Start Date"
                                    name="messageStartDate"
                                    value={formData.messageStartDate}
                                    onChange={(v, n) => handleChange({ target: { name: n, value: v } })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <AppDateTimePicker
                                    label="Stop Date"
                                    name="messageStopDate"
                                    value={formData.messageStopDate}
                                    onChange={(v, n) => handleChange({ target: { name: n, value: v } })}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Time Entry
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Minimum threshold (minutes)"
                                    name="timeMinThresholdMinutes"
                                    type="number"
                                    value={formData.timeMinThresholdMinutes}
                                    onChange={handleChange}
                                    inputProps={{ step: '1', min: '0' }}
                                    helperText="Entries shorter than this are saved as 0 (set to 0 to never zero-out)"
                                    placeholder="5"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Round up to (minutes)"
                                    name="timeRoundUpToMinutes"
                                    type="number"
                                    value={formData.timeRoundUpToMinutes}
                                    onChange={handleChange}
                                    inputProps={{ step: '1', min: '0' }}
                                    helperText="Round entries up to the nearest multiple (set to 0 to disable rounding)"
                                    placeholder="15"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Maximum duration (hours)"
                                    name="timeMaxDurationHours"
                                    type="number"
                                    value={formData.timeMaxDurationHours}
                                    onChange={handleChange}
                                    inputProps={{ step: '0.25', min: '0' }}
                                    helperText="Entries above this cap are silently capped to this value"
                                    placeholder="24"
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <ActionsRow>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </ActionsRow>
                </>
            )}
            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default SystemConfig;
